import classNames from "classnames";
import React, {
    HTMLAttributes, ReactElement, ReactNode,
    useMemo,
} from "react";
import TableBody from "./Table";
import TableValue, { TableValueType } from "./TableValue";
import TableControls, { TableControlsWithTypes } from "./controls";

import TableRowAction, { TableRowActionType } from "./TableRowAction";
import TableRowSelector, { Type as TableRowSelectorType } from "./TableRowSelector";

import Styles from "./Table.module.scss";
import {
    ContainsId, Events, TableContext, TableData,
} from "./base";
import { ErrorView } from "../../../pages/error/Error";
import TableEmpty from "./TableEmpty";
import TableHeader from "./TableHeader";

export interface TableContainerProps<T extends ContainsId> extends HTMLAttributes<HTMLDivElement> {
    children?: ReactNode;
    data: T[] | undefined;
    isLoading?: boolean;
    borderVariant?: "primary" | "secondary" | "none";
    error?: Error;
}

const TableContainer = React.forwardRef(<T extends ContainsId>(
    args: TableContainerProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const {
        children,
        className,
        isLoading,
        error,
        data,
        borderVariant,
        ...rest
    } = args;

    const tableData = useMemo(() => new TableData<ContainsId>(data, { isLoading }), [data, isLoading]);

    tableData.events.useEvent(Events.UPDATED_EMPTY_VIEW);

    return (
        <TableContext.Provider value={tableData}>
            {tableData.isEmptyData && tableData.emptyElement != null ? (
                <>
                    {tableData.emptyElement}
                    {children}
                </>
            ) : (
                <div
                    className={classNames(
                        Styles.container,
                        className,
                        Styles[`container-border-${borderVariant}`],
                    )}
                    ref={ref}
                    {...rest}
                >
                    {error ? (
                        <ErrorView
                            title="Error"
                            className={Styles.error}
                            message={error.message}
                            stacktrace={error.stack?.split("\n")}
                        />
                    ) : children}
                </div>
            )}
        </TableContext.Provider>
    );
});

TableContainer.defaultProps = {
    isLoading: false,
    error: undefined,
    borderVariant: "none",
};

type TableType<T extends ContainsId = {id: string}> = (
    props: TableContainerProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof TableContainer>;

interface TableChildren<T extends ContainsId> {
    Table: typeof TableBody;
    TableValue: TableValueType<T>;
    TableControls: TableControlsWithTypes<T>;
    Action: TableRowActionType<T>;
    Empty: typeof TableEmpty;
    Header: typeof TableHeader
    RowSelector: TableRowSelectorType<T>
}

type TableWithTypes<T extends ContainsId> = TableType<T> & TableChildren<T>;

const Table = TableContainer as unknown as TableWithTypes<ContainsId>;
Table.Table = TableBody;
Table.TableValue = TableValue as TableValueType<ContainsId>;
Table.TableControls = TableControls as TableControlsWithTypes<ContainsId>;
Table.Action = TableRowAction as TableRowActionType<ContainsId>;
Table.Empty = TableEmpty;
Table.Header = TableHeader;
Table.RowSelector = TableRowSelector as TableRowSelectorType<ContainsId>;

export default (() => Table) as <T extends ContainsId>() => TableWithTypes<T>;
